import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { PickupPointNetwork } from '@/lib/graphql/generated/graphql';
import {
  ShippingPolicy,
  ShippingPolicyProps,
} from '@/lib/types/shipping-policy';
import { storeAtom } from '@/states/store.state';
import { Currency } from '@/types/currency';
import { Store } from '@/types/store';
import Button from '@appchoose/button';
import { Checkbox } from '@appchoose/checkbox';
import Input, { InputAdornment } from '@appchoose/input';
import RadioGroup, { RadioGroupItem } from '@appchoose/radio-group';
import { useAtomValue } from 'jotai';
import { Info } from 'lucide-react';

interface NetworkSelectorProps {
  disabled?: boolean;
  value: PickupPointNetwork;
  onChange: (network: PickupPointNetwork) => void;
}

export const getNetworkLabel = (network: PickupPointNetwork): string => {
  return network === PickupPointNetwork.MondialRelay
    ? 'Mondial Relay'
    : 'Shop2Shop';
};

const NetworkSelector: React.FC<NetworkSelectorProps> = ({
  disabled = false,
  value,
  onChange,
}) => (
  <RadioGroup
    value={value}
    onValueChange={onChange}
    className="flex flex-col gap-3"
  >
    {Object.values(PickupPointNetwork).map((network) => (
      <div className="flex items-center space-x-3" key={network}>
        <RadioGroupItem
          value={network}
          id={`network-${network}`}
          disabled={disabled}
          className="h-[16px] w-[16px]"
        />
        <Label
          htmlFor={`network-${network}`}
          key={network}
          className="cursor-pointer text-sm font-medium text-gray-700 peer-disabled:cursor-default peer-disabled:text-gray-300 peer-data-[state=checked]:text-gray-900 peer-data-[state=checked]:peer-disabled:text-gray-300"
        >
          {getNetworkLabel(network)}
        </Label>
      </div>
    ))}
  </RadioGroup>
);

interface PickupPointDisplayProps extends ShippingPolicyProps {
  embedded?: boolean;
  hideEnableToggle?: boolean;
  onCancel?: () => void;
  cancelLabel?: string;
  confirmLabel?: string;
  isSubmitting?: boolean;
}

export const PickupPointDisplay: React.FC<PickupPointDisplayProps> = ({
  policy: initialPolicy,
  onPolicyChange,
  isLocked = false,
  open,
  onOpenChange,
  embedded = false,
  hideEnableToggle = false,
  onCancel,
  cancelLabel,
  confirmLabel,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  // const { toast } = useToast();

  const [tempPolicy, setTempPolicy] = useState<ShippingPolicy>(initialPolicy);
  const [error, setError] = useState<string | null>(null);
  const isFormDisabled = isLocked || isSubmitting;

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting && !nextOpen) return;
    onOpenChange(nextOpen);
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    if (onCancel) onCancel();
    else onOpenChange(false);
  };

  const validateForm = (): boolean => {
    const error = validateDeliveryAmount(tempPolicy.pickupPointDeliveryPrice);
    setError(error);

    return !error;
  };

  const handleSave = () => {
    if (validateForm()) {
      onPolicyChange(tempPolicy);
    }
  };

  const validateDeliveryAmount = (value: number | null): string | null => {
    if (!tempPolicy.pickupPointEnabled) {
      return null;
    }

    if (value === null || value < 0) {
      return t('components.pickup_point_configurator.error_minimum_amount', {
        currency: store === Store.FR ? Currency.EUR : Currency.USD,
      });
    }
    return null;
  };

  const handleDeliveryAmountChange = (value: number | null) => {
    const newValue = isNaN(value as number) ? null : value;

    setTempPolicy({
      ...tempPolicy,
      pickupPointDeliveryPrice: newValue,
    });

    setError(validateDeliveryAmount(newValue));
  };

  const togglePickupPointEnabled = () => {
    setTempPolicy((current) => {
      const newEnabled = !current.pickupPointEnabled;

      return {
        ...current,
        pickupPointEnabled: newEnabled,
        pickupPointNetwork: newEnabled ? PickupPointNetwork.MondialRelay : null,
        pickupPointMinimumOrderAmount: newEnabled
          ? current.pickupPointMinimumOrderAmount
          : null,
        minimumOrderSplitByDeliveryMode: newEnabled
          ? current.minimumOrderSplitByDeliveryMode
          : false,
        minimumOrderEnabled: newEnabled
          ? current.minimumOrderEnabled
          : current.minimumOrderAmount !== null,
      };
    });
  };

  const handleNetworkChange = (network: PickupPointNetwork) => {
    setTempPolicy({
      ...tempPolicy,
      pickupPointEnabled: true,
      pickupPointNetwork: network,
    });
  };

  const content = (
    <>
      <div className="mb-6 space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
          <p className="text-blue-800">
            {t(
              'components.pickup_point_configurator.operational_notice.description'
            )}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {!hideEnableToggle && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="pickup-point-enabled"
                checked={tempPolicy.pickupPointEnabled}
                disabled={isFormDisabled}
                onCheckedChange={togglePickupPointEnabled}
                className="h-[16px] w-[16px]"
              />
              <label
                htmlFor="pickup-point-enabled"
                className={`text-sm font-medium ${
                  isFormDisabled
                    ? 'cursor-not-allowed text-gray-400'
                    : 'cursor-pointer'
                }`}
              >
                {t('components.pickup_point_configurator.enable')}
              </label>
            </div>
          )}
          {tempPolicy.pickupPointNetwork && (
            <div className="pl-7">
              <NetworkSelector
                disabled={isFormDisabled}
                value={tempPolicy.pickupPointNetwork}
                onChange={handleNetworkChange}
              />
            </div>
          )}

          {tempPolicy.pickupPointEnabled && (
            <div className="pl-7">
              <label className="mb-2 block text-xs font-semibold text-gray-600">
                {t('components.pickup_point_configurator.amount')}
              </label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  value={tempPolicy.pickupPointDeliveryPrice ?? ''}
                  disabled={isFormDisabled}
                  onChange={(e) => {
                    const newValue =
                      e.target.value === '' ? null : Number(e.target.value);
                    handleDeliveryAmountChange(newValue);
                  }}
                  onBlur={() => {
                    setError(
                      validateDeliveryAmount(
                        tempPolicy.pickupPointDeliveryPrice
                      )
                    );
                  }}
                  className={`bg-white ${
                    error ? 'border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder={t(
                    'components.pickup_point_configurator.threshold_placeholder'
                  )}
                  startAdornment={
                    <InputAdornment position="start">
                      {store === Store.FR ? Currency.EUR : Currency.USD}
                    </InputAdornment>
                  }
                />
              </div>
              {error ? (
                <div className="mt-1 text-xs text-red-500">{error}</div>
              ) : (
                <div className="mt-1 text-xs text-gray-500">
                  {t('components.pickup_point_configurator.amount_help')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button
          type="button"
          appearance="outline"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          {cancelLabel ?? t('general.cancel')}
        </Button>
        <Button
          type="button"
          appearance="primary"
          onClick={handleSave}
          disabled={isFormDisabled}
        >
          {confirmLabel ?? t('general.validate')}
        </Button>
      </DialogFooter>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('ui-v2.settings.blocks-shipping-policy.pickup-point.title')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t(
              'components.pickup_point_configurator.operational_notice.description'
            )}
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
};
